import { useMutation, useQueryClient } from '@tanstack/react-query';

import { AssignmentKey } from '#assignment/keys';
import { useErrorMessage } from '#error-message/useErrorMessage';

import type { CreateGradeInput } from './gradeService';

import { GradeService } from './gradeService';
import { GradeKey } from './keys';

/**
 * `assignmentId` is not part of the wire payload — it is carried alongside the
 * input purely so the related assignment queries (which embed the response's
 * gradeId) can be invalidated after grading.
 */
type CreateGradeVariables = CreateGradeInput & { assignmentId?: number };

export function useCreateGrade() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (variables: CreateGradeVariables) =>
      GradeService.createGrade({
        name: variables.name,
        desc: variables.desc,
        // Assignment grades are always percentage-valued.
        valueType: variables.valueType ?? 'percentage',
        value: variables.value,
        respId: variables.respId,
        userId: variables.userId,
        groupId: variables.groupId,
      }).then((response) => response.data),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: [GradeKey.GRADE, data.id] });
      queryClient.invalidateQueries({ queryKey: [GradeKey.USER_GRADES] });
      // Auto-graded and response-graded grades carry no groupId, so the group
      // they surface in is only known server-side — invalidate by key prefix.
      queryClient.invalidateQueries({ queryKey: [GradeKey.GROUP_GRADES] });
      queryClient.invalidateQueries({ queryKey: [GradeKey.USER_GROUP_GRADES] });

      if (variables.assignmentId !== undefined) {
        queryClient.invalidateQueries({
          queryKey: [AssignmentKey.ASSIGNMENT_SUBMISSIONS, variables.assignmentId],
        });
        queryClient.invalidateQueries({
          queryKey: [AssignmentKey.ASSIGNMENT, variables.assignmentId],
        });
      }
    },
  });

  const errorMessage = useErrorMessage(mutation.error);

  return { ...mutation, errorMessage };
}
